import { axiosInstance } from "@/lib/axios-instance";
import { IWorkspace } from "@/types/data-models";
import { lookbookAccessHeaders } from "@/lib/lookbook-access";

interface ToggleResponse {
  status: string;
  result: { raise_board_enabled: boolean; portal_slug: string | null };
}

interface BulkSetResponse {
  status: string;
  matched: number;
  modified: number;
}

/**
 * Which board an action is scoped to. Exactly one field is meaningful: a
 * lookbook's public id wins if both are somehow set, matching the backend,
 * which rejects a request carrying both.
 */
export interface BoardScope {
  slug?: string;
  lookbookPublicId?: string;
}

/** A document a lookbook has been told to share. */
export interface LookbookSharedFile {
  _id: string;
  file_name: string;
  file_type: string;
  file_size?: string;
  time_stamp?: string;
  upload_source?: string;
  /**
   * The document's storage url, sent only for a document on the entry's
   * allowlist. Optional because a payload served by a backend older than this
   * change carries none — those fall back to the proxy url.
   */
  file_url?: string;
}

/**
 * What this lookbook shows for this deal.
 *
 * Only ever present on a /lb/ payload. Absent on /rb/ and /global, where
 * resolveLookbookVisibility falls back to profile-only — which is what those
 * boards have always shown.
 */
export interface LookbookDealSharing {
  profile: boolean;
  data_room: boolean;
  reports: boolean;
  documents: LookbookSharedFile[];
  report_keys: string[];
}

export interface RaiseBoardApplication {
  _id: string;
  applicant_company:
    | string
    | {
        _id: string;
        company_name?: string;
        short_bio?: string;
        industry?: string[];
        country?: string;
        company_website?: string;
        background_image?: string;
        stage?: number;
        logo_url?: string;
        business_model?: string | string[];
        revenue_model?: string | string[];
      };
  pertains_to_opportunity:
    | string
    | {
        _id: string;
        opportunity_name?: string;
        portal_slug?: string;
        raise_board_enabled?: boolean;
      };
  raise_board?: boolean;
  upvote_count?: number;
  comment_count?: number;
  short_bio?: string;
  industry?: string[];
  deal_info?: Array<{ field_name: string; field_value: unknown; field_state?: string }>;
  attempting_to_raise?: string;
  currency?: string;
  stage?: number;
  business_model?: string | string[];
  revenue_model?: string | string[];
  company_website?: string;
  city?: string;
  country?: string;
  year_of_inception?: string;
  problem?: string;
  solution?: string;
  traction?: string;
  objective?: string;
  linkedIn?: string;
  twitter?: string;
  created_on?: string;
  sharing?: LookbookDealSharing;
  [key: string]: unknown;
}

/** One selectable value in a board filter, with how many deals carry it. */
export interface RaiseBoardFacet {
  value: string;
  count: number;
}

/**
 * Options for the board's filter dropdowns, computed server-side over the whole
 * board so selecting one filter never empties the others.
 *
 * `industries` are raw GICS sub-industry codes — the code→name table lives on
 * the frontend (`src/lib/gics-level5.json`), so labels are resolved here.
 */
export interface RaiseBoardFacets {
  stages: RaiseBoardFacet[];
  industries: RaiseBoardFacet[];
  countries: RaiseBoardFacet[];
}

export interface RaiseBoardPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

/** Browse controls for a board. Omitting them all returns every deal. */
export interface RaiseBoardBrowseParams {
  page?: number;
  limit?: number;
  search?: string;
  stage?: string[];
  industry?: string[];
  country?: string[];
}

export interface RaiseBoardBySlugResponse {
  status: string;
  result: {
    opportunity: IWorkspace;
    applications: RaiseBoardApplication[];
    /** Both present only when the caller asked for a page. */
    pagination?: RaiseBoardPagination;
    facets?: RaiseBoardFacets;
  };
}

export type RaiseBoardGlobalApplication = RaiseBoardApplication;

export interface RaiseBoardGlobalResponse {
  status: string;
  result: {
    applications: RaiseBoardGlobalApplication[];
  };
}

/**
 * One row of the /rb directory. Counts are the board's public totals across
 * every deal it lists; a board with no deals is never returned.
 */
export interface RaiseBoardSummary {
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  deal_count: number;
  upvote_count: number;
  comment_count: number;
  /** How many of its deals it also publishes to /global. */
  global_deal_count: number;
  last_deal_added: string | null;
}

export interface RaiseBoardsResponse {
  status: string;
  result: { boards: RaiseBoardSummary[] };
}

interface ToggleVoteResponse {
  status: string;
  voted: boolean;
  upvote_count: number;
}

interface MyVotesResponse {
  status: string;
  application_ids: string[];
}

/**
 * The surface a comment was written on. `lookbook` is implied by the scope;
 * the other three all live on the shared public/global thread and can only be
 * told apart by this. It is a label the client asserts, never a permission —
 * the lookbook scope is the only visibility boundary.
 */
export type RaiseBoardCommentOrigin =
  | "public_board"
  | "global_board"
  | "lookbook"
  | "deal_profile";

export interface RaiseBoardComment {
  _id: string;
  /** Null for a comment left by a signed-out guest on a lookbook. */
  user: string | null;
  user_name?: string;
  text: string;
  /** Absent on comments written before origins were recorded. */
  origin?: RaiseBoardCommentOrigin;
  created_on: string;
}

interface CommentsResponse {
  status: string;
  comments: RaiseBoardComment[];
}

interface AddCommentResponse {
  status: string;
  comment: RaiseBoardComment;
  comment_count: number;
}

export const raiseBoardApi = {
  toggle: async (
    opportunityId: string,
    enabled: boolean
  ): Promise<ToggleResponse> => {
    const response = await axiosInstance.post("/toggle_raise_board", {
      opportunity_id: opportunityId,
      enabled,
    });
    return response.data;
  },

  bulkSet: async (
    opportunityId: string,
    companyIds: string[],
    raiseBoard: boolean
  ): Promise<BulkSetResponse> => {
    const response = await axiosInstance.post("/bulk_set_raise_board", {
      opportunity_id: opportunityId,
      company_ids: companyIds,
      raise_board: raiseBoard,
    });
    return response.data;
  },

  bulkSetGlobalVisibility: async (
    opportunityId: string,
    companyIds: string[],
    isPublic: boolean
  ): Promise<BulkSetResponse> => {
    const response = await axiosInstance.post("/bulk_set_global_visibility", {
      opportunity_id: opportunityId,
      company_ids: companyIds,
      global_visibility: isPublic,
    });
    return response.data;
  },

  getGlobal: async (): Promise<RaiseBoardGlobalResponse> => {
    const response = await axiosInstance.get<RaiseBoardGlobalResponse>(
      "/fetch_global_raise_board"
    );
    return response.data;
  },

  getBoards: async (): Promise<RaiseBoardSummary[]> => {
    const response = await axiosInstance.get<RaiseBoardsResponse>(
      "/fetch_raise_boards"
    );
    return response.data?.result?.boards ?? [];
  },

  /**
   * Omit `browse` to load the whole board — the deal profile page does that,
   * because it finds its one deal inside the full list.
   */
  getBySlug: async (
    slug: string,
    browse?: RaiseBoardBrowseParams
  ): Promise<RaiseBoardBySlugResponse> => {
    // Multi-value filters go over the wire comma-joined; the API accepts that
    // or repeated params. Empty selections are dropped so the URL stays clean.
    const csv = (values?: string[]) =>
      values && values.length > 0 ? values.join(",") : undefined;

    const response = await axiosInstance.get<RaiseBoardBySlugResponse>(
      "/fetch_raise_board_by_slug",
      {
        params: {
          slug,
          page: browse?.page,
          limit: browse?.limit,
          search: browse?.search || undefined,
          stage: csv(browse?.stage),
          industry: csv(browse?.industry),
          country: csv(browse?.country),
        },
      }
    );
    return response.data;
  },

  sendDealEmail: async (params: {
    slug: string;
    applicationId: string;
    recipientEmail: string;
    industries?: string;
  }): Promise<{ status: string }> => {
    const response = await axiosInstance.post("/send_raise_board_deal_email", {
      slug: params.slug,
      application_id: params.applicationId,
      recipient_email: params.recipientEmail,
      industries: params.industries,
    });
    // Treat a 2xx-with-error body as a failure so the caller's onSuccess can't
    // fire on a send that didn't happen.
    if (response.data?.status !== "success") {
      throw new Error(response.data?.message || "Failed to send deal details");
    }
    return response.data;
  },

  /*
   * Votes and comments are scoped. Omitting the lookbook acts on the public
   * board and global board, which share one tally; passing one acts on that
   * lookbook's private tally and leaves the public numbers untouched.
   *
   * The identifier is the lookbook's *public* id — the one in the /lb/<id> URL
   * a visitor was given. A lookbook's internal ObjectId is never put on the
   * wire to these public surfaces; only the manager-only endpoints in
   * lookbook-api.ts use it.
   */

  toggleVote: async (
    applicationId: string,
    lookbookPublicId?: string
  ): Promise<ToggleVoteResponse> => {
    const response = await axiosInstance.post<ToggleVoteResponse>(
      "/toggle_raise_board_vote",
      {
        application_id: applicationId,
        lookbook_public_id: lookbookPublicId,
      },
      { headers: lookbookAccessHeaders(lookbookPublicId) }
    );
    return response.data;
  },

  getMyVotes: async (lookbookPublicId?: string): Promise<string[]> => {
    const response = await axiosInstance.get<MyVotesResponse>(
      "/my_raise_board_votes",
      {
        params: lookbookPublicId
          ? { lookbook_public_id: lookbookPublicId }
          : undefined,
        headers: lookbookAccessHeaders(lookbookPublicId),
      }
    );
    return response.data?.application_ids ?? [];
  },

  getComments: async (
    applicationId: string,
    lookbookPublicId?: string
  ): Promise<RaiseBoardComment[]> => {
    const response = await axiosInstance.get<CommentsResponse>(
      "/raise_board_comments",
      {
        params: {
          application_id: applicationId,
          ...(lookbookPublicId
            ? { lookbook_public_id: lookbookPublicId }
            : {}),
        },
        headers: lookbookAccessHeaders(lookbookPublicId),
      }
    );
    return response.data?.comments ?? [];
  },

  addComment: async (
    applicationId: string,
    text: string,
    lookbookPublicId?: string,
    origin?: RaiseBoardCommentOrigin
  ): Promise<AddCommentResponse> => {
    const response = await axiosInstance.post<AddCommentResponse>(
      "/add_raise_board_comment",
      {
        application_id: applicationId,
        text,
        lookbook_public_id: lookbookPublicId,
        origin,
      },
      { headers: lookbookAccessHeaders(lookbookPublicId) }
    );
    return response.data;
  },

  /*
   * Guest engagement — the signed-out versions of toggleVote, getMyVotes and
   * addComment, for lookbooks whose owner allows it. Lookbook-only; the guest
   * is identified by the visitor id this browser keeps (lib/lookbook-access).
   */

  toggleGuestVote: async (
    applicationId: string,
    lookbookPublicId: string,
    visitorId: string
  ): Promise<ToggleVoteResponse> => {
    const response = await axiosInstance.post<ToggleVoteResponse>(
      "/toggle_lookbook_guest_vote",
      {
        application_id: applicationId,
        lookbook_public_id: lookbookPublicId,
        visitor_id: visitorId,
      },
      { headers: lookbookAccessHeaders(lookbookPublicId) }
    );
    return response.data;
  },

  getGuestVotes: async (
    lookbookPublicId: string,
    visitorId: string
  ): Promise<string[]> => {
    const response = await axiosInstance.get<MyVotesResponse>(
      "/lookbook_guest_votes",
      {
        params: { lookbook_public_id: lookbookPublicId, visitor_id: visitorId },
        headers: lookbookAccessHeaders(lookbookPublicId),
      }
    );
    return response.data?.application_ids ?? [];
  },

  addGuestComment: async (
    applicationId: string,
    text: string,
    lookbookPublicId: string,
    visitorId: string,
    name?: string
  ): Promise<AddCommentResponse> => {
    const response = await axiosInstance.post<AddCommentResponse>(
      "/add_lookbook_guest_comment",
      {
        application_id: applicationId,
        text,
        lookbook_public_id: lookbookPublicId,
        visitor_id: visitorId,
        name,
      },
      { headers: lookbookAccessHeaders(lookbookPublicId) }
    );
    return response.data;
  },

  deleteComment: async (
    commentId: string
  ): Promise<{ status: string; comment_count: number }> => {
    const response = await axiosInstance.delete(
      `/raise_board_comment/${commentId}`
    );
    return response.data;
  },

  /*
   * Subscriptions — "email me when a deal is added to this board".
   *
   * Scoped like votes and comments, with one difference: naming no board is an
   * error rather than shorthand for the global board. /global aggregates every
   * workspace, so subscribing to it would be a firehose; the backend rejects
   * it, and nothing in the UI offers it.
   */

  toggleSubscription: async (
    scope: BoardScope
  ): Promise<{ status: string; subscribed: boolean }> => {
    const response = await axiosInstance.post("/toggle_raise_board_subscription", {
      slug: scope.lookbookPublicId ? undefined : scope.slug,
      lookbook_public_id: scope.lookbookPublicId,
    }, { headers: lookbookAccessHeaders(scope.lookbookPublicId) });
    return response.data;
  },

  getMySubscription: async (scope: BoardScope): Promise<boolean> => {
    const response = await axiosInstance.get("/my_raise_board_subscription", {
      params: scope.lookbookPublicId
        ? { lookbook_public_id: scope.lookbookPublicId }
        : { slug: scope.slug },
      headers: lookbookAccessHeaders(scope.lookbookPublicId),
    });
    return !!response.data?.subscribed;
  },

  /**
   * One-click unsubscribe from the link in an email. Public — the token is the
   * credential, because whoever clicks it is often not signed in on that
   * device.
   */
  unsubscribe: async (token: string): Promise<void> => {
    await axiosInstance.post("/unsubscribe_raise_board", { token });
  },
};
